import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Skeleton, Text } from '@mantine/core'
import { InfiniteData } from '@tanstack/react-query'

import classes from '../Campaign.module.scss'

import { CampaignOrganisation } from '@percent/workplace-giving/api/campaigns/getCampaignOrganisations/getCampaignOrganisations.types'
import { useColorTheme } from '@percent/workplace-giving/common/hooks/useColorTheme/useColorTheme'
import { useDonationForm } from '@percent/workplace-giving/common/hooks/useDonationForm/useDonationForm'
import { OrganisationCard } from '@percent/workplace-giving/common/components/OrganisationCard/OrganisationCard'
import { OrganisationCardSkeleton } from '@percent/workplace-giving/common/components/OrganisationCard/OrganisationCardSkeleton'
import { useAuth } from '@percent/workplace-giving/common/hooks'
import {
  getCountryCodeFromAuthState,
  getStateCodeFromAuthState
} from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { useRestrictedDonationConfig } from '@percent/workplace-giving/common/hooks/useRestrictedDonationConfig/useRestrictedDonationConfig'
import { BlockedRegionsConfig } from '@percent/workplace-giving/api/donation/getDonationPartnerConfig/getDonationPartnerConfig.types'

type CampaignOrganisationsProps = Readonly<{
  campaignId: string
  data: InfiniteData<CampaignOrganisation[]> | undefined
  isFetching: boolean
}>

export function CampaignOrganisations({ campaignId: _campaignId, data, isFetching }: CampaignOrganisationsProps) {
  const { t } = useTranslation()
  const { theme } = useColorTheme()
  const { state } = useAuth()
  const { blockedRegions } = useRestrictedDonationConfig()

  const blockedCountryCodes = useMemo(() => {
    return blockedRegions.map((region: BlockedRegionsConfig) => region.countryCode)
  }, [blockedRegions])

  const { handleDonate, DonationFormWrapper } = useDonationForm('regular')

  if (isFetching) {
    return (
      <Box className={classes.SectionWrapper} data-testid="campaign-organisations-section-loader">
        <Skeleton height="2rem" width="55%" />
        <Box className={classes.SectionTilesContentWrapper}>
          <OrganisationCardSkeleton />
        </Box>
      </Box>
    )
  }

  if (data?.pages?.[0]?.length) {
    return (
      <Box className={classes.SectionWrapper} data-testid="campaign-organisations-section">
        <Text className={classes.SectionTitle}>{t('workplace_giving.campaigns.supportNonprofits')}</Text>
        <Box className={classes.SectionTilesContentWrapper}>
          {data.pages.map((p: CampaignOrganisation[]) =>
            p.map((o: CampaignOrganisation) => (
              <OrganisationCard
                handleDonate={handleDonate}
                trackingOrigin="Campaign"
                key={o.organisationId}
                organisation={{
                  ...o,
                  address: {
                    line1: o.address?.line1 || null,
                    line2: o.address?.line2 || null,
                    city: o.address?.city || null,
                    postalCode: o.address?.postalCode || null,
                    state: o.address?.state || null
                  }
                }}
                userOrigin={{
                  country: getCountryCodeFromAuthState(state),
                  state: getStateCodeFromAuthState(state)
                }}
                blockedCountryCodes={blockedCountryCodes}
              />
            ))
          )}
        </Box>
        <DonationFormWrapper
          params={{
            primaryColor: theme.primary400
          }}
        />
      </Box>
    )
  }

  return null
}
